import {
  Phone,
  Mail,
  Globe,
  Check,
  X,
  ArrowRight,
  Loader2,
  Building,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import Logo from "./Logo";
import StepIndicator from "./StepIndicator";
import BusinessSearchInput from "./BusinessSearchInput";
import { useEffect, useState,useCallback  } from "react";
import { onboardingService } from "../utils/googlePlaces";

export default function StepOneForm({
  formData,
  handleInputChange,
  handleSendPhoneOTP,
  handleVerifyPhoneOTP,
  handleSendEmailOTP,
  handleVerifyEmailOTP,
  isPhoneLoading,
  isEmailLoading,
  phoneOtpSent,
  phoneOtpVerified,
  emailOtpSent,
  emailOtpVerified,
  handleNextStep,
  isTraining,
  handleCancel,
  isEmailValid,
  onValidationChange,
  emailOtpError,
  phoneOtpError,
  clearEmailOtpError,
  clearPhoneOtpError,
  handleEmailVerification,
  isCheckingEmail,
  emailExistsError,
  setEmailExistsError
  
}) {
  // Local state for OTP and phone number inputs
  const [phoneOtp, setPhoneOtp] = useState("");
  const [emailOtp, setEmailOtp] = useState("");
  const [ukPhoneNumber, setUkPhoneNumber] = useState(
    formData.phoneNumber ? formData.phoneNumber.replace('+44', '') : ""
  );
  
  // const [emailExistsError, setEmailExistsError] = useState("");
  // const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  // Google Business Profile states
  const [selectedGoogleBusiness, setSelectedGoogleBusiness] = useState(null);
  const [isGoogleBusinessSelected, setIsGoogleBusinessSelected] = useState(false);
  
  // Website validation states
  const [isWebsiteValid, setIsWebsiteValid] = useState(null);
  const [isWebsiteReachable, setIsWebsiteReachable] = useState(null);
  const [isCheckingWebsite, setIsCheckingWebsite] = useState(false);
  const [websiteError, setWebsiteError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // State to track if the user has attempted to submit the form
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  // Helper function to generate greeting
  const generateGreeting = (businessName) => {
    return `Hello! Welcome to ${businessName}. How can I help you today?`;
  };

  // Website URL validation function
  const isValidUrl = (url) => {
    // console.log('--- isValidUrl ---', url);
    if (!url || url.trim() === '') return false;
    
    const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
    
    if (!urlRegex.test(url)) {
      return false;
    }

    try {
      const prefixedUrl = url.startsWith('http') ? url : `https://${url}`;
      new URL(prefixedUrl);
      return true;
    } catch (e) {
      return false;
    }
  };

 

 

// Fast website reachability check using ERPNext API
const checkWebsiteReachability = async (url) => {
  // console.log('--- checkWebsiteReachability ---', url);
  if (!url || !isValidUrl(url)) {
    return { reachable: false, error: "Invalid URL format" };
  }

  try {
    setIsCheckingWebsite(true);
    setWebsiteError("");
    
    // Call ERPNext API to check reachability
    const result = await onboardingService.checkWebsiteReachability(url);
    
    return {
      reachable: result.reachable,
      error: result.error,
      note: result.note
    };
    
  } catch (error) {
    console.error('Website reachability check failed:', error);
    
    // Fallback: assume valid URLs are reachable if API fails
    if (isValidUrl(url)) {
      return { 
        reachable: true, 
        error: null,
        note: "Unable to verify connectivity, but URL format is valid"
      };
    }
    
    return { 
      reachable: false, 
      error: "Unable to verify website availability - please check the URL format" 
    };
  } finally {
    setIsCheckingWebsite(false);
  }
};

// Handle website reachability check
// Handle website reachability check with useCallback to fix dependency issue
const handleWebsiteReachabilityCheck = useCallback(async () => {
  // console.log('--- handleWebsiteReachabilityCheck ---');
  if (!formData.websiteAddress || !isValidUrl(formData.websiteAddress)) {
    setIsWebsiteReachable(false);
    setWebsiteError("Please enter a valid website URL");
    return;
  }

  // Clear business name if user has invalid GBP text and is using website approach
  if (!formData.isGBP && formData.businessName.trim() && !selectedGoogleBusiness) {
    // console.log('Clearing business name - user is using website approach with invalid GBP text');
    handleBusinessNameChange('');
    setSelectedGoogleBusiness(null);
    setIsGoogleBusinessSelected(false);
  }

  const result = await checkWebsiteReachability(formData.websiteAddress);
  setIsWebsiteReachable(result.reachable);
  setWebsiteError(result.error || "");
}, [formData.websiteAddress, formData.isGBP, formData.businessName, selectedGoogleBusiness]);

  // Auto-check website reachability when URL is valid and user stops typing
  useEffect(() => {
    if (!formData.websiteAddress.trim()) {
      setIsWebsiteValid(null);
      setIsWebsiteReachable(null);
      setWebsiteError("");
      return;
    }

    const isValid = isValidUrl(formData.websiteAddress);
    setIsWebsiteValid(isValid);
    // console.log('Website URL validity:: 173', isValid);

    if (isValid) {
      setIsWebsiteReachable(null);
      setWebsiteError("");
      //  console.log('Website URL validity:: 178', isValid);
      const timeoutId = setTimeout(() => {
        // console.log('Website URL validity:: 180', isValid);
        handleWebsiteReachabilityCheck();
      }, 500);

      return () => clearTimeout(timeoutId);
    } else {
      setIsWebsiteReachable(null);
      setWebsiteError("");
    }
  }, [formData.websiteAddress]);

  // FIXED: Handle Google Business Profile selection
  const handleGoogleBusinessSelect = (businessData) => {
    // console.log('📦 Raw business data received:', businessData);
    
    setSelectedGoogleBusiness(businessData);
    setIsGoogleBusinessSelected(true);
    
    // Update form data with Google Business info
    handleInputChange("businessName", businessData.businessName);
    
    // CRITICAL: Set the isGBP flag to true
    handleInputChange("isGBP", true);
    
    // Store the complete Google Business data in OnboardingApp state
    handleInputChange("googleBusinessProfile", {
      placeId: businessData.placeId,
      businessName: businessData.businessName,
      address: businessData.address,
      phone: businessData.phone,
      internationalPhone: businessData.internationalPhone,
      website: businessData.website,
      rating: businessData.rating,
      totalReviews: businessData.totalReviews,
      businessStatus: businessData.businessStatus,
      businessTypes: businessData.businessTypes, // Use this consistently
      location: businessData.location,
      openingHours: businessData.openingHours,
      photos: businessData.photos,
      reviews: businessData.reviews,
      priceLevel: businessData.priceLevel,
      plusCode: businessData.plusCode,
      isGoogleBusinessLinked: true,
      linkedAt: new Date().toISOString(),
      
      // NEW: Additional fields for AI training
      editorialSummary: businessData.editorialSummary,
      formattedPhoneNumber: businessData.phone, // Already formatted
      internationalPhoneNumber: businessData.internationalPhone,
      
      // Use website data if available      
      generatedGreeting: generateGreeting(businessData.businessName),
      
    });
    
    // console.log('✅ GBP data stored successfully');

    // Auto-fill website if available - ALWAYS replace when GBP is selected
    if (businessData.website) {
      try {
        const urlObj = new URL(businessData.website.startsWith('http') ? businessData.website : `https://${businessData.website}`);
        const mainUrl = `${urlObj.protocol}//${urlObj.host}`;
        // console.log('businessData.website', businessData.website);
        // console.log('mainUrl (base domain only)', mainUrl);
        handleInputChange("websiteAddress", mainUrl);
        // console.log('🌐 Auto-filled website:', mainUrl);
      } catch (e) {
        // Fallback if URL parsing fails
        const mainUrl = businessData.website.split('?')[0];
        handleInputChange("websiteAddress", mainUrl);
      }
    }
    
    // Auto-fill phone if available and not already filled
    if (businessData.internationalPhone && !formData.phoneNumber.trim()) {
      const phoneNumber = businessData.internationalPhone.replace(/\s+/g, '');
      if (phoneNumber.startsWith('+44')) {
        const ukNumber = phoneNumber.replace('+44', '');
        setUkPhoneNumber(ukNumber);
        handleInputChange("phoneNumber", phoneNumber);
        // console.log('📞 Auto-filled phone:', phoneNumber);
      }
    }
  };
  
  // Handle manual business name input
  const handleBusinessNameChange = (value) => {
    handleInputChange("businessName", value);

    // If user is typing manually, clear Google Business selection and set GBP flag to false
    if (isGoogleBusinessSelected && value !== selectedGoogleBusiness?.businessName) {
      setSelectedGoogleBusiness(null);
      setIsGoogleBusinessSelected(false);
      handleInputChange("googleBusinessProfile", null);
      handleInputChange("isGBP", false);
      // Clear the phone number and website when business selection changes
      setUkPhoneNumber("");
      handleInputChange("phoneNumber", "");
      handleInputChange("websiteAddress", "");
      // console.log('🔄 Cleared GBP data due to manual input');
    }

    // If user clears the business name completely, clear Google Business data
    if (!value.trim()) {
      setSelectedGoogleBusiness(null);
      setIsGoogleBusinessSelected(false);
      handleInputChange("googleBusinessProfile", null);
      handleInputChange("isGBP", false);
      // Clear the phone number and website when business name is cleared
      setUkPhoneNumber("");
      handleInputChange("phoneNumber", "");
      handleInputChange("websiteAddress", "");
      // console.log('🗑️ Cleared all GBP data - empty input');
    }
  };

  // CORRECTED validation logic with real-time debugging
  const isBusinessNameFilled = formData.businessName.trim() !== '';
  const isWebsiteFilled = formData.websiteAddress.trim() !== '';
  const isWebsiteValidAndFilled = isWebsiteFilled && isValidUrl(formData.websiteAddress);
  
  // Check if user has selected a Google Business Profile properly
  const hasValidGoogleBusiness = isGoogleBusinessSelected && selectedGoogleBusiness && formData.isGBP;
  const hasValidWebsite = isWebsiteValidAndFilled && isWebsiteReachable === true;
  
  // Primary field is filled if user has EITHER valid Google Business OR valid website
  const isPrimaryFieldFilled = hasValidGoogleBusiness || hasValidWebsite;
  
  // Form is complete only if both OTPs are verified AND primary field requirement is met
  // const isFormComplete = phoneOtpVerified && emailOtpVerified && isPrimaryFieldFilled;
  const isFormComplete = phoneOtpVerified && emailOtpVerified && isPrimaryFieldFilled && !emailExistsError;

  
  // Show validation ONLY when OTPs verified but NEITHER GBP nor website is properly filled
  const shouldShowValidationMessage = phoneOtpVerified && emailOtpVerified && !hasValidGoogleBusiness && !hasValidWebsite;
//   if(process.env.REACT_APP_NODE_ENV === 'development')
//   {
//    console.log('REAL-TIME VALIDATION STATUS:', {
//     businessName: formData.businessName,
//     isBusinessNameFilled,
//     isGoogleBusinessSelected,
//     selectedGoogleBusiness: !!selectedGoogleBusiness,
//     formDataIsGBP: formData.isGBP,
//     hasValidGoogleBusiness,
//     isWebsiteFilled,
//     isWebsiteValidAndFilled,
//     isWebsiteReachable,
//     hasValidWebsite,
//     isPrimaryFieldFilled,
//     phoneOtpVerified,
//     emailOtpVerified,
//     isFormComplete,
//     shouldShowValidationMessage,
//     // Add extractedInfo status
//     hasExtractedInfo: !!formData.extractedInfo,
//     extractedInfoFields: formData.extractedInfo ? Object.keys(formData.extractedInfo) : []
//   }); 
// }

  // Add immediate state logging when GBP changes
  useEffect(() => {
    // if(process.env.REACT_APP_NODE_ENV === 'development')
    // {
    //   console.log('GBP STATE CHANGED:', {
    //   isGoogleBusinessSelected,
    //   selectedGoogleBusiness: !!selectedGoogleBusiness,
    //   formDataIsGBP: formData.isGBP,
    //   hasValidGoogleBusiness: isGoogleBusinessSelected && selectedGoogleBusiness && formData.isGBP
    //     });
    // }
      
    
  }, [isGoogleBusinessSelected, selectedGoogleBusiness, formData.isGBP]);

    // Add this useEffect in StepOneForm.jsx to reset local state when parent resets
  useEffect(() => {
    // Reset local state when parent form data is cleared
    if (!formData.phoneNumber && !formData.email && !formData.otp && !formData.emailOtp) {
      setPhoneOtp("");
      setEmailOtp("");
      setUkPhoneNumber("");
    }
  }, [formData.phoneNumber, formData.email, formData.otp, formData.emailOtp]);
  
  // Pass validation status to parent
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isFormComplete);
    }
  }, [isFormComplete, onValidationChange]);

  // Handle phone OTP input change
  const handlePhoneOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    setPhoneOtp(value);
    handleInputChange("otp", value);

    // Clear error when user starts typing
    if (phoneOtpError && clearPhoneOtpError) {
      clearPhoneOtpError();
    }
  };

  // Handle email OTP input change
  const handleEmailOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    setEmailOtp(value);
    handleInputChange("emailOtp", value);

      // Clear error when user starts typing
    if (emailOtpError && clearEmailOtpError) {
      clearEmailOtpError();
    }
  };

  // Handle UK phone number input change
  const handleUkPhoneNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    setUkPhoneNumber(value);
    handleInputChange("phoneNumber", "+44" + value);
  };

  // UK phone number validation
  const isUkPhoneNumberValid = ukPhoneNumber.length === 10;
  
  // Handle form submission
const handleSubmit = async (event) => {
  event.preventDefault();
  setHasAttemptedSubmit(true);
  
  if (isWebsiteFilled && isWebsiteValid && isWebsiteReachable === false) {
    return;
  }
  
  if (!isFormComplete) {
    return;
  }
  // Set loading state immediately
  setIsSubmitting(true);

  try {
    let extractedInfo = {
      businessName: formData.businessName,
      industry: "Unknown",
      description: "",
      services: [],
      socialMedia: {},
      openingHours: [],
      address: "",
      phone: "",
      rating: null,
      totalReviews: 0
    };

    // Get data from website first - this is our primary source
    if (formData.websiteAddress && isWebsiteReachable === true) {
      const scrapedData = await onboardingService.scrapeWebsite(formData.websiteAddress);
      // console.log('🔍 Raw website scraped data:', scrapedData);
      
      if (scrapedData.success && scrapedData.data) {

         if (!formData.isGBP && scrapedData.data.business_name) {
          extractedInfo.businessName = scrapedData.data.business_name;
          // Also update the form data to reflect the scraped business name
          handleInputChange("businessName", scrapedData.data.business_name);
        }

        extractedInfo.industry = scrapedData.data.industry || extractedInfo.industry;
        extractedInfo.description = scrapedData.data.businessOverview || extractedInfo.description;
        extractedInfo.services = scrapedData.data.productsServices || [];
        extractedInfo.socialMedia = scrapedData.data.socialMedia || {};
        
        if (scrapedData.data.openingHours?.length > 0) {
          extractedInfo.openingHours = scrapedData.data.openingHours;
        }
        if (scrapedData.data.address) {
          extractedInfo.address = scrapedData.data.address;
        }
        if (scrapedData.data.phone) {
          extractedInfo.phone = scrapedData.data.phone;
        }
        
        // console.log('📄 Primary data set from website:', extractedInfo);
      }
    }

    // Only use GBP data for non-core fields that are still empty
    if (formData.isGBP && formData.googleBusinessProfile) {
      const gbp = formData.googleBusinessProfile;
      // console.log('🏢 Checking GBP data for missing fields:', gbp);
      
      if (!extractedInfo.openingHours || extractedInfo.openingHours.length === 0) {
        extractedInfo.openingHours = gbp.openingHours?.weekdayText || [];
      }
      if (!extractedInfo.address) {
        extractedInfo.address = gbp.address || "";
      }
      if (!extractedInfo.phone) {
        extractedInfo.phone = gbp.formattedPhone || gbp.internationalPhone || "";
      }
      if (extractedInfo.rating === null) {
        extractedInfo.rating = gbp.rating || null;
      }
      if (!extractedInfo.totalReviews) {
        extractedInfo.totalReviews = gbp.totalReviews || 0;
      }
    }

    // console.log('📝 Setting final extractedInfo:', extractedInfo);
    
    // SOLUTION: Pass the extractedInfo directly to handleNextStep
    // Let the parent component handle the state update
    handleNextStep(extractedInfo);
    
  } catch (error) {
    console.error('Error in form submission:', error);
    setIsSubmitting(false); // Reset loading on error
  }
};

  return (
    <div className="min-h-screen flex items-start justify-center pt-1 p-4" style={{ backgroundColor: '#ffffff' }}>
      <div className="bg-white rounded-2xl  p-8 w-full max-w-md">
        <Logo />
        <StepIndicator currentStep={1} />

        <h2 className="text-xl font-semibold text-gray-900 mb-6">
          Let's get started
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Business Name Input with Google Business Profile Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Building size={16} className="inline mr-2" />
              Business Name (Google Business Profile)
            </label>
            
            <BusinessSearchInput
              value={formData.businessName}
              onChange={handleBusinessNameChange}
              onBusinessSelect={handleGoogleBusinessSelect}
              placeholder="Search for your business on Google..."
              className={isBusinessNameFilled ? "border-green-500" : "border-gray-300"}
            />
          </div>

          {/* Website Input - UPDATED: Always show and always allow editing */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Globe size={16} className="inline mr-2" />
              Website Address
              {!formData.isGBP && <span className="text-red-500"> *</span>}
              {selectedGoogleBusiness?.website && (
                <span className="text-xs text-green-600 ml-2">(Auto-filled from Google - You can edit)</span>
              )}
            </label>
            <div className="relative">
             <input
                type="text"
                value={formData.websiteAddress}
                onChange={(e) => handleInputChange("websiteAddress", e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all pr-10 ${
                  isWebsiteReachable === true 
                    ? "border-green-500" 
                    : isWebsiteReachable === false || (isWebsiteValid === false && isWebsiteFilled)
                      ? "border-red-500" 
                      : isWebsiteValid === true && isWebsiteReachable === null
                        ? "border-yellow-500"
                        : "border-gray-300"
                }`}
                placeholder="https://yourwebsite.com"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {isCheckingWebsite && (
                  <Loader2 size={20} className="text-blue-500 animate-spin" />
                )}
                {!isCheckingWebsite && isWebsiteReachable === true && (
                  <Check size={20} className="text-green-500" />
                )}
                {!isCheckingWebsite && (isWebsiteReachable === false || (isWebsiteValid === false && isWebsiteFilled)) && (
                  <X size={20} className="text-red-500" />
                )}
                {!isCheckingWebsite && isWebsiteValid === true && isWebsiteReachable === null && isWebsiteFilled && (
                  <AlertCircle size={20} className="text-yellow-500" />
                )}
              </div>
            </div>
            
            {/* Website validation messages */}
            {isWebsiteFilled && isWebsiteValid === false && (
              <div className="mt-2 flex items-start space-x-2">
                <AlertCircle size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-600">
                  Please enter a valid website URL (e.g., yourwebsite.com or https://yourwebsite.com)
                </p>
              </div>
            )}
            
            {isWebsiteValid && isWebsiteReachable === false && websiteError && (
              <div className="mt-2 flex items-start space-x-2">
                <AlertCircle size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm text-red-600 mb-2">
                    {websiteError}
                  </p>
                  <button
                    type="button"
                    onClick={handleWebsiteReachabilityCheck}
                    disabled={isCheckingWebsite}
                    className="inline-flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
                  >
                    <RefreshCw size={14} className={isCheckingWebsite ? "animate-spin" : ""} />
                    <span>Retry check</span>
                  </button>
                </div>
              </div>
            )}
            
            {isWebsiteValid && isWebsiteReachable === true && (
              <div className="mt-2 flex items-center space-x-2">
                <Check size={16} className="text-green-500" />
                <p className="text-sm text-green-600">
                  Website is reachable and looks good!
                </p>
              </div>
            )}
            
           
          </div>
          
          {/* Enhanced validation messages based on field states */}
          {shouldShowValidationMessage && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center mt-0.5">
                    <span className="text-white text-xs font-bold">!</span>
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-blue-800 mb-1">
                    Business information required
                  </h3>
                  <p className="text-sm text-blue-700">
                    Please <strong>select your business from Google Business Profile</strong> above, or provide a <strong>valid Website Address</strong> below to continue.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Specific validation for when user has attempted to submit but requirements not met */}
          {hasAttemptedSubmit && !isPrimaryFieldFilled && !shouldShowValidationMessage && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <AlertCircle size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-red-800 font-medium">
                    Cannot proceed - Missing required business information
                  </p>
                  <p className="text-xs text-red-700 mt-1">
                    {!hasValidGoogleBusiness && !isWebsiteFilled 
                      ? "You must either select a Google Business Profile from the dropdown above OR provide a valid website address."
                      : !hasValidGoogleBusiness && isWebsiteFilled && isWebsiteReachable === false 
                        ? "Your website is not reachable. Please select a Google Business Profile from the dropdown above OR provide a working website address."
                        : !hasValidGoogleBusiness && isWebsiteFilled && isWebsiteValid === false
                          ? "Invalid website format. Please select a Google Business Profile from the dropdown above OR provide a valid website address."
                          : "Please select a Google Business Profile from the dropdown above OR provide a valid website address."
                    }
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Email Section */}
        
          <div>
  <label className="block text-sm font-medium text-gray-700 mb-2">
    <Mail size={16} className="inline mr-2" />
    Email Address *
  </label>
  <div className="flex space-x-2">
      <div className="relative flex-1">
        <input
          type="email"
          value={formData.email}
          onChange={(e) => {
            handleInputChange("email", e.target.value);
            // Clear errors when user starts typing
            if (emailExistsError) setEmailExistsError("");
          }}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all pr-10 ${
            emailExistsError ? "border-red-500" : "border-gray-300"
          }`}
          placeholder="your@email.com"
          required
          disabled={emailOtpSent}
        />
        {isCheckingEmail && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <Loader2 size={16} className="text-blue-500 animate-spin" />
          </div>
        )}
      </div>
      {formData.email && !emailOtpVerified && !emailExistsError && (
        <button
          type="button"
          onClick={() => handleEmailVerification(formData.email, setEmailExistsError)}
          disabled={isEmailLoading || emailOtpSent || !isEmailValid || isCheckingEmail}
          className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center space-x-2"
        >
          {isCheckingEmail || isEmailLoading ? (
            <Loader2 size={16} className="animate-spin" />
          ) : null}
          <span>{emailOtpSent ? "Sent" : "Verify"}</span>
        </button>
      )}
      {emailOtpVerified && (
        <span className="px-4 py-3 bg-green-100 text-green-600 rounded-lg flex items-center space-x-2">
          <Check size={16} />
        </span>
      )}
    </div>
  
 
  {/* Email Exists Error Message */}
    {emailExistsError && (
      <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex items-start space-x-2">
          <AlertCircle size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
             
            <p className="text-xs text-red-700 mt-1">{emailExistsError}</p>
            <div className="mt-2 flex space-x-2">
              <button
                type="button"
                onClick={() => {
                  handleInputChange("email", "");
                  setEmailExistsError("");
                }}
                className="text-xs text-blue-600 hover:text-blue-800 underline"
              >
                Use different email
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
  </div>
          {/* Email OTP Input */}
        {emailOtpSent && !emailOtpVerified && (
        <div className="animate-fadeIn">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Enter Email OTP
          </label>
          <div className="flex space-x-2">
            <input
              type="text"
              value={emailOtp}
              onChange={handleEmailOtpChange}
              className={`flex-1 px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                emailOtpError ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Enter 6-digit code"
              maxLength="6"
              inputMode="numeric"
            />
            <button
              type="button"
              onClick={handleVerifyEmailOTP}
              disabled={isEmailLoading || emailOtp.length !== 6}
              className="px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center space-x-2"
            >
              {isEmailLoading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Check size={16} />
              )}
              <span>Verify</span>
            </button>
          </div>
          {/* Email OTP Error Message */}
          {emailOtpError && (
          <div className="mt-2 flex items-start space-x-2">
            <AlertCircle size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-red-600">{emailOtpError}</p>
              <button
                type="button"
                onClick={() => {
                  setEmailOtp("");
                  handleInputChange("emailOtp", "");
                  clearEmailOtpError(); // Use the passed function
                }}
                className="mt-1 text-xs text-blue-600 hover:text-blue-800 underline"
              >
                Clear and try again
              </button>
            </div>
          </div>
        )}
        </div>
      )}
          
          {/* Phone Number Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Phone size={16} className="inline mr-2" />
              Phone Number *
              {selectedGoogleBusiness?.phone && (
                <span className="text-xs text-green-600 ml-2">(Auto-filled from Google)</span>
              )}
            </label>
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-1 px-3 py-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 flex-shrink-0">
                <span>+44</span>
                <img
                  src="https://placehold.co/24x16/cccccc/000000?text=UK"
                  alt="UK Flag"
                  className="w-6 h-4 rounded-sm"
                />
              </div>
              <input
                type="tel"
                value={ukPhoneNumber}
                onChange={handleUkPhoneNumberChange}
                className="flex-1 min-w-0 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="7123456789"
                maxLength="10"
                required
                disabled={phoneOtpSent}
              />
              {ukPhoneNumber && !phoneOtpVerified && (
                <button
                  type="button"
                  onClick={handleSendPhoneOTP}
                  disabled={isPhoneLoading || phoneOtpSent || !isUkPhoneNumberValid}
                  className="flex-shrink-0 px-3 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center space-x-2"
                >
                  {isPhoneLoading && !phoneOtpSent ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : null}
                  <span>{phoneOtpSent ? "Sent" : "Verify"}</span>
                </button>
              )}
              {phoneOtpVerified && (
                <span className="flex-shrink-0 px-4 py-3 bg-green-100 text-green-600 rounded-lg flex items-center justify-center space-x-2">
                  <Check size={16} />
                </span>
              )}
            </div>
          </div>
          
          {/* Phone OTP Input */}
          {phoneOtpSent && !phoneOtpVerified && (
          <div className="animate-fadeIn">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Enter Phone OTP
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={phoneOtp}
                onChange={handlePhoneOtpChange}
                className={`flex-1 px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                  phoneOtpError ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Enter 6-digit code"
                maxLength="6"
                inputMode="numeric"
              />
              <button
                type="button"
                onClick={handleVerifyPhoneOTP}
                disabled={isPhoneLoading || phoneOtp.length !== 6}
                className="px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center space-x-2"
              >
                {isPhoneLoading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Check size={16} />
                )}
                <span>Verify</span>
              </button>
            </div>
            {/* Phone OTP Error Message */}
              {phoneOtpError && (
                <div className="mt-2 flex items-start space-x-2">
                  <AlertCircle size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-red-600">{phoneOtpError}</p>
                    <button
                      type="button"
                      onClick={() => {
                        setPhoneOtp("");
                        handleInputChange("otp", "");
                        clearPhoneOtpError(); // Use the passed function
                      }}
                      className="mt-1 text-xs text-blue-600 hover:text-blue-800 underline"
                    >
                      Clear and try again
                    </button>
                  </div>
                </div>
              )}
          </div>
        )}
          {/* Action buttons */}
          <div className="flex justify-between mt-6">
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors flex items-center space-x-2"
            >
              <span>Cancel</span>
            </button>
           <button
              type="submit"
              disabled={isTraining || isSubmitting || !isFormComplete || isCheckingWebsite}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center space-x-2"
            >
              {(isTraining || isSubmitting) ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  <span>Training Agent...</span>
                </>
              ) : (
                <>
                  <span>Next Step</span>
                  <ArrowRight size={20} />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}